"use client";
import { useRef, useState } from "react";
import { Box, Stack, Typography, Avatar, IconButton } from "@mui/material";
import Image from "next/image";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Pagination } from "swiper/modules";
import type { Swiper as SwiperType } from "swiper";
import { mockReview } from "@/data/review_mock_data";
import { ReviewType } from "@/types/schema";

// Import Swiper styles
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";

const ReviewCard = ({ review, author, authorImage }: {
  review: string;
  author?: string;
  authorImage?: string;
}) => {
  return (
    <Box
      sx={{
        background: `linear-gradient(180deg, #070C0F 0%, rgba(7, 12, 15, 0) 110%), #4D140F`,
        border: "1px solid #17232B",
        borderRadius: "16px",
        p: 3,
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
        gap: 6,
        height: '160px',
      }}
    >
      {/* Review Content */}
      <Stack gap={2}>
        {/* Stars */}
        <Stack direction="row" gap={0.5}>
          {[...Array(5)].map((_, index) => (
            <Box key={index} sx={{ width: 14, height: 14 }}>
              <Image
                src="/svg/star.svg"
                width={14}
                height={14}
                alt="star"
              />
            </Box>
          ))}
        </Stack>

        {/* Review Text */}
        <Typography
          variant="body1"
          sx={{
            color: "#FFFFFF",
            fontSize: "18px",
            lineHeight: "26px",
            fontWeight: 400,
          }}
        >
          {review}
        </Typography>
      </Stack>

      {/* Author Info */}
      {author && (
        <Stack direction="row" alignItems="center" gap={1.5}>
          <Avatar
            src={authorImage}
            sx={{
              width: 36,
              height: 36,
              bgcolor: "#2d3748"
            }}
          >
            {author.charAt(0)}
          </Avatar>
          <Typography
            variant="body2"
            sx={{
              color: "#9E9E9F",
              fontSize: "16px",
              lineHeight: "24px",
              fontWeight: 400,
            }}
          >
            {author}
          </Typography>
        </Stack>
      )}
    </Box>
  );
};

const PaginationDots = ({
  totalSlides,
  currentIndex,
  onDotClick
}: {
  totalSlides: number;
  currentIndex: number;
  onDotClick: (index: number) => void;
}) => (
  <Stack direction="row" gap={1} justifyContent="center">
    {[...Array(totalSlides)].map((_, index) => (
      <Box
        key={index}
        sx={{
          width: 20,
          height: 4,
          borderRadius: "99px",
          bgcolor: index === currentIndex ? "#BC1918" : "rgba(255, 255, 255, 0.2)",
          cursor: "pointer",
          transition: "background-color 0.3s ease",
        }}
        onClick={() => onDotClick(index)}
      />
    ))}
  </Stack>
);

const GarlicIcon = () => {
  return <Box
    component="img"
    src="/images/reviews/garlic.png"
    alt="Decorative flower"
    sx={{
      position: 'absolute',
      top: 0,
      left: 0,
      width: '155px',
      height: '133px',
      mt: 14,
      opacity: 0.4,
    }}
  />;
};

const GingerIcon = () => {
  return <Box
    component="img"
    src="/images/reviews/ginger.png"
    alt="Decorative flower"
    sx={{
      position: 'absolute',
      bottom: 0,
      right: 0,
      width: '315px',
      height: '150px',
      opacity: 0.3,
    }}
  />;
};

export const EveryoneAgreesSection = () => {
  const desktopSwiperRef = useRef<SwiperType | null>(null);
  const mobileSwiperRef = useRef<SwiperType | null>(null);
  const [currentIndex, setCurrentIndex] = useState(0);

  // Filter text reviews for this section
  const textReviews = mockReview.filter(review =>
    review.reviewType === ReviewType.TEXT && review.author
  );

  // Group reviews into pages of 6 items each
  const reviewsPerPage = 6;
  const totalPages = Math.ceil(textReviews.length / reviewsPerPage);
  const reviewPages = [];

  for (let i = 0; i < totalPages; i++) {
    const startIndex = i * reviewsPerPage;
    const endIndex = startIndex + reviewsPerPage;
    reviewPages.push(textReviews.slice(startIndex, endIndex));
  }

  const handlePrevious = () => {
    const newIndex = currentIndex === 0 ? totalPages - 1 : currentIndex - 1;
    desktopSwiperRef.current?.slideTo(newIndex);
    mobileSwiperRef.current?.slideTo(newIndex);
    setCurrentIndex(newIndex);
  };

  const handleNext = () => {
    const newIndex = currentIndex === totalPages - 1 ? 0 : currentIndex + 1;
    desktopSwiperRef.current?.slideTo(newIndex);
    mobileSwiperRef.current?.slideTo(newIndex);
    setCurrentIndex(newIndex);
  };

  const handleSlideChange = (swiper: SwiperType) => {
    setCurrentIndex(swiper.activeIndex);

    // Auto-scroll to Left Arrow button on mobile
    if (window.innerWidth < 900) { // Mobile breakpoint
      setTimeout(() => {
        // Find the Left Arrow button (mobile navigation)
        const leftArrowButton = document.querySelector('[data-mobile-left-arrow]');

        if (leftArrowButton) {
          leftArrowButton.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
            inline: 'nearest'
          });
        }
      }, 300); // Small delay to allow slide transition to complete
    }
  };

  const handleDotClick = (index: number) => {
    desktopSwiperRef.current?.slideTo(index);
    mobileSwiperRef.current?.slideTo(index);
  };

  return (
    <Box
      sx={{
        py:{ xs: 15, md: 25 },
        px: { xs: 4, md: 0 },
        position: "relative",
      }}
    >
      <GarlicIcon />
      <GingerIcon />

      <Stack
        alignItems="center"
        gap={{ xs: 6, md: 7.5 }}
        sx={{
          maxWidth: { xs: "100%", md: "996px" },
          mx: "auto"
        }}
      >
        {/* Title */}
        <Typography
          variant="h2"
          sx={{
            background: "linear-gradient(90deg, #CD7858 0%, #FFD969 100%)",
            backgroundClip: "text",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            fontSize: { xs: "24px", md: "32px" },
            lineHeight: { xs: "32px", md: "38px" },
            fontWeight: 600,
            textAlign: "center",
            width: "100%"
          }}
        >
          ทุกคนพูดเป็นเสียงเดียวกันว่า
        </Typography>

        {/* Reviews Swiper Container */}
        <Box
          sx={{
            width: "100%",
            position: "relative",
          }}
        >
          {/* Desktop Layout - Horizontal Stack with Navigation */}
          <Stack
            direction="row"
            justifyContent="center"
            alignItems="center"
            gap={3}
            sx={{
              display: { xs: "none", md: "flex" },
              width: "100%",
            }}
          >
            {/* Left Arrow Button - Desktop */}
            <IconButton
              onClick={handlePrevious}
              sx={{
                width: 40,
                height: 40,
                backgroundColor: "#17232B",
                borderRadius: "50px",
                color: "#FFFFFF",
                "&:hover": {
                  backgroundColor: "#17232B",
                  opacity: 0.8,
                },
              }}
            >
              <Box
                component="img"
                src="/images/products/arrow-left.svg"
                alt="Previous"
                sx={{ width: 20, height: 20 }}
              />
            </IconButton>

            {/* Swiper Container - Desktop */}
            <Box
              sx={{
                width: "100%",
                maxWidth: "996px",
                position: "relative",
              }}
            >
              <Swiper
                modules={[Navigation, Pagination]}
                spaceBetween={0}
                slidesPerView={1}
                loop={false}
                onBeforeInit={(swiper) => {
                  desktopSwiperRef.current = swiper;
                }}
                onSlideChange={handleSlideChange}
                style={{
                  width: "100%",
                  height: "auto",
                }}
              >
                {reviewPages.map((pageReviews, pageIndex) => (
                  <SwiperSlide key={pageIndex}>
                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: "1fr 1fr",
                        gridTemplateRows: "repeat(3, 1fr)",
                        gap: 2.5,
                        gridAutoFlow: "column",
                        width: "100%",
                      }}
                    >
                      {pageReviews.slice(0, 6).map((review, index) => (
                        <ReviewCard
                          key={`page-${pageIndex}-${index}`}
                          review={review.review}
                          author={review.author}
                          authorImage={review.authorImage}
                        />
                      ))}
                    </Box>
                  </SwiperSlide>
                ))}
              </Swiper>
            </Box>

            {/* Right Arrow Button - Desktop */}
            <IconButton
              onClick={handleNext}
              sx={{
                width: 40,
                height: 40,
                backgroundColor: "#17232B",
                borderRadius: "50px",
                color: "#FFFFFF",
                "&:hover": {
                  backgroundColor: "#17232B",
                  opacity: 0.8,
                },
              }}
            >
              <Box
                component="img"
                src="/images/products/arrow-right.svg"
                alt="Next"
                sx={{ width: 20, height: 20 }}
              />
            </IconButton>
          </Stack>

          {/* Mobile Layout - Full Width with Overlaid Arrows */}
          <Box
            sx={{
              display: { xs: "block", md: "none" },
              position: "relative",
              width: "100%",
            }}
          >
            <Swiper
              modules={[Navigation, Pagination]}
              spaceBetween={0}
              slidesPerView={1}
              loop={false}
              autoHeight={true}
              onBeforeInit={(swiper) => {
                mobileSwiperRef.current = swiper;
              }}
              onSlideChange={handleSlideChange}
              style={{
                width: "100%",
                height: "auto",
              }}
            >
              {reviewPages.map((pageReviews, pageIndex) => (
                <SwiperSlide key={pageIndex}>
                  <Stack
                    gap={2}
                    sx={{
                      width: "100%",
                    }}
                  >
                    {pageReviews.map((review, index) => (
                      <ReviewCard
                        key={`mobile-page-${pageIndex}-${index}`}
                        review={review.review}
                        author={review.author}
                        authorImage={review.authorImage}
                      />
                    ))}
                  </Stack>
                </SwiperSlide>
              ))}
            </Swiper>
          </Box>
        </Box>

        {/* Pagination Dots */}
        {totalPages > 1 && (
          <Stack alignItems="center" gap={6}>
            <PaginationDots
              totalSlides={totalPages}
              currentIndex={currentIndex}
              onDotClick={handleDotClick}
            />

            {/* Mobile Arrow Buttons - Below Pagination */}
            <Stack
              direction="row"
              gap={3}
              sx={{
                display: { xs: "flex", md: "none" },
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              {/* Left Arrow Button - Mobile (Below Dots) */}
              <IconButton
                onClick={handlePrevious}
                data-mobile-left-arrow
                sx={{
                  width: 40,
                  height: 40,
                  backgroundColor: "#17232B",
                  borderRadius: "50px",
                  color: "#FFFFFF",
                  "&:hover": {
                    backgroundColor: "#17232B",
                    opacity: 0.8,
                  },
                }}
              >
                <Box
                  component="img"
                  src="/images/products/arrow-left.svg"
                  alt="Previous"
                  sx={{ width: 20, height: 20 }}
                />
              </IconButton>

              {/* Right Arrow Button - Mobile (Below Dots) */}
              <IconButton
                onClick={handleNext}
                sx={{
                  width: 40,
                  height: 40,
                  backgroundColor: "#17232B",
                  borderRadius: "50px",
                  color: "#FFFFFF",
                  "&:hover": {
                    backgroundColor: "#17232B",
                    opacity: 0.8,
                  },
                }}
              >
                <Box
                  component="img"
                  src="/images/products/arrow-right.svg"
                  alt="Next"
                  sx={{ width: 20, height: 20 }}
                />
              </IconButton>
            </Stack>
          </Stack>
        )}
      </Stack>
    </Box>
  );
};